import { createContext, useContext } from 'react'

export interface AuthState {
  authEnabled: boolean
  isAuthenticated: boolean
  /** session is being restored from secure-store on boot */
  isLoading: boolean
  /** an interactive login (IdP redirect + token exchange) is in progress */
  isAuthenticating: boolean
  /** last login error message, if any (shown on the login screen) */
  error: string | null
  /**
   * ROPC-вход не прошёл, потому что у аккаунта висит required action (обычно временный пароль,
   * выданный админом). Довести такой вход можно только браузерным флоу — экран входа
   * переключает кнопку на `login()`, чтобы Keycloak показал страницу смены пароля.
   */
  mustSetPassword: boolean
  /**
   * Контур текущей сессии: `portal` — сотрудник, `cabinet` — внешний пользователь
   * (поставщик, турагент), `null` — сессии нет. У контуров разные realm, поэтому гейт по
   * нему решает, какие маршруты человеку вообще доступны: внешнему — только кабинет.
   */
  sessionKind: 'portal' | 'cabinet' | null
  /** start the OIDC redirect flow; resolves true on success, false on cancel/error */
  login: () => Promise<boolean>
  /** Тот же браузерный флоу, но в realm внешних — вход в кабинет контрагента. */
  loginCabinet: () => Promise<boolean>
  /**
   * Seamless in-app sign-in (OIDC Direct Access Grant / ROPC): exchange the
   * username+password entered in our own form for tokens at Keycloak's token
   * endpoint — no browser redirect, no Keycloak login page. Resolves true on
   * success, false on bad credentials/error (message in `error`).
   * Requires "Direct access grants" enabled on the Keycloak client.
   */
  loginWithPassword: (
    username: string,
    password: string,
    /** Контур: `portal` (сотрудник, по умолчанию) или `cabinet` (бизнес-партнёр). */
    kind?: 'portal' | 'cabinet',
  ) => Promise<boolean>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
